import React, { useEffect, useState } from "react";
import { Clock, AlertTriangle, Trash2, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";

const risk = {
  High: { ring: "text-rose-500", chip: "bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/20" },
  Medium: { ring: "text-amber-500", chip: "bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/20" },
  Normal: { ring: "text-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/20" },
};

const daysLeft = (dateStr) => {
  const diff = new Date(dateStr) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

const Ring = ({ value, tone }) => {
  const size = 44, stroke = 4, r = (size - stroke) / 2, c = 2 * Math.PI * r;
  const offset = c - (Math.min(100, Math.max(0, value)) / 100) * c;
  return (
    <svg width={size} height={size} className="shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} className="text-slate-200 dark:text-slate-700" stroke="currentColor" fill="none" />
      <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} fill="none" stroke="currentColor" strokeLinecap="round" className={tone}
        strokeDasharray={c} strokeDashoffset={offset} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text x="50%" y="52%" textAnchor="middle" dominantBaseline="middle" className="fill-slate-700 dark:fill-slate-200 text-[10px] font-bold font-mono">{value}%</text>
    </svg>
  );
};

export default function UpcomingDeadlines() {
  const [items, setItems] = useState([]);
  const load = () => endpoints.listDeadlines().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this deadline?")) return;
    try { await endpoints.deleteDeadline(id); toast.success("Deadline deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };
  const clearAll = async () => {
    if (!window.confirm(`Delete all ${items.length} deadlines?`)) return;
    try { await endpoints.clearAllDeadlines(); toast.success("All deadlines cleared"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="upcoming-deadlines" className="glass-card rounded-3xl p-5 sm:p-6 h-full card-hover">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-lg bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-300 flex items-center justify-center">
          <Clock className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <h2 className="font-display font-bold text-[15px] tracking-tight">Upcoming Deadlines</h2>
          <p className="text-xs text-slate-500">Prioritized by risk</p>
        </div>
        {items.length > 0 && (
          <button onClick={clearAll} data-testid="deadlines-clear-all"
            className="text-[11px] text-slate-400 hover:text-rose-600 inline-flex items-center gap-1">
            <Trash2 className="h-3 w-3" /> Clear all
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-sm text-slate-500">No deadlines yet.</div>
      ) : (
        <ul className="space-y-3">
          {items.map((d) => {
            const r = risk[d.risk_level] || risk.Normal;
            return (
              <li key={d.id} data-testid={`deadline-${d.id}`} className="group flex items-center gap-3 rounded-2xl border border-slate-200/70 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 p-3">
                <Ring value={d.progress || 0} tone={r.ring} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold truncate">{d.title}</span>
                    <Badge variant="outline" className={`text-[10px] rounded-full ${r.chip}`}>{d.risk_level}</Badge>
                  </div>
                  <div className="text-xs text-slate-500 truncate">{d.client}</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <div className="text-[11px] uppercase font-semibold text-slate-400 tracking-wider">In</div>
                    <div className="text-sm font-bold font-mono">{daysLeft(d.due_date)}d</div>
                  </div>
                  <button onClick={() => del(d.id)} data-testid={`delete-deadline-${d.id}`}
                    className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600 transition" title="Delete">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                {d.risk_level === "High" && <AlertTriangle className="h-4 w-4 text-rose-500" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
